import { useState, useEffect, useCallback, useRef } from "react";
import { Canvas, Group } from "fabric";
import { MeasurementPoint, MeasurementTool } from "../types/measurements";
import { MeasurementService } from "@/services/measurement-service";
import { pixelsToMillimeters, calculatePixelsPerMm } from "@/utils/measurement-utils";
import { CustomFabricObject, CustomCanvas } from "@/types/fabric";

interface UnifiedMeasurementProps {
  canvas: Canvas | null;
  activeTool: MeasurementTool;
  onToast?: (message: { title: string; description: string }) => void;
  scale?: number;
  onMeasurementComplete?: (tool: 'ruler' | 'angle') => void;
}

export const useUnifiedMeasurements = ({
  canvas,
  activeTool,
  onToast,
  scale = 1,
  onMeasurementComplete
}: UnifiedMeasurementProps) => {
  const [distancePoints, setDistancePoints] = useState<MeasurementPoint[]>([]);
  const [anglePoints, setAnglePoints] = useState<MeasurementPoint[]>([]);
  const [previewObjects, setPreviewObjects] = useState<any[]>([]);
  const [cursorPosition, setCursorPosition] = useState<MeasurementPoint | null>(null);

  // Track if we need to force a preview update when cursor position changes
  const shouldUpdatePreview = useRef(false);
  
  // Initialize measurement calibration when canvas is ready
  useEffect(() => {
    if (!canvas) return;
    
    // Force an initial calibration calculation to ensure measurements are accurate
    calculatePixelsPerMm(null, canvas as CustomCanvas);
    
    // Setup a listener for canvas object additions to recalibrate if needed
    const handleObjectAdded = () => {
      calculatePixelsPerMm(null, canvas as CustomCanvas);
    };
    
    canvas.on('object:added', handleObjectAdded);
    
    return () => {
      canvas.off('object:added', handleObjectAdded);
    };
  }, [canvas]);

  const cleanupPreviewObjects = useCallback(() => {
    if (!canvas) return;
    
    previewObjects.forEach(obj => canvas.remove(obj));
    setPreviewObjects([]);
    
    canvas.getObjects().forEach(obj => {
      if ((obj as any).data?.isPreview) {
        canvas.remove(obj);
      }
    });
    
    canvas.renderAll();
  }, [canvas, previewObjects]);

  const resetMeasurement = useCallback((type: 'ruler' | 'angle' | 'all') => {
    if (!canvas) return;

    cleanupPreviewObjects();
    
    canvas.getObjects().forEach(obj => {
      if ((obj as any).data?.isTemporary) {
        canvas.remove(obj);
      }
    });
    canvas.renderAll();
    
    if (type === 'ruler' || type === 'all') {
      setDistancePoints([]);
    }
    if (type === 'angle' || type === 'all') {
      setAnglePoints([]);
    }
    
    // Reset cursor position and preview flag when resetting measurements
    setCursorPosition(null);
    shouldUpdatePreview.current = false;
  }, [canvas, cleanupPreviewObjects]);

  // New function to handle escape key press
  const handleEscapeKey = useCallback(() => {
    // Only cancel if we have an active measurement in progress
    if (distancePoints.length > 0 || anglePoints.length > 0) {
      // Clean up any preview and temporary objects
      cleanupPreviewObjects();
      
      if (canvas) {
        canvas.getObjects().forEach(obj => {
          if ((obj as any).data?.isTemporary) {
            canvas.remove(obj);
          }
        });
        canvas.renderAll();
      }
      
      // Reset the measurement points
      setDistancePoints([]);
      setAnglePoints([]);
      
      // Reset cursor position and preview flag
      setCursorPosition(null);
      shouldUpdatePreview.current = false;
      
      // Show toast notification
      onToast?.({
        title: "Measurement canceled",
        description: "Measurement has been canceled",
      });
    }
  }, [distancePoints, anglePoints, cleanupPreviewObjects, canvas, onToast]);

  // Add effect for keyboard escape key handling
  useEffect(() => {
    // Only add listener if a measurement is in progress or measurement tool is active
    const isMeasuring = activeTool === 'ruler' || activeTool === 'angle';
    const hasMeasurementInProgress = distancePoints.length > 0 || anglePoints.length > 0;
    
    if (isMeasuring || hasMeasurementInProgress) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          handleEscapeKey();
        }
      };
      
      window.addEventListener('keydown', handleKeyDown);
      
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [activeTool, distancePoints, anglePoints, handleEscapeKey]);

  const updateMeasurementScale = useCallback((newScale: number) => {
    if (!canvas) return;
    const zoom = canvas.getZoom();
    MeasurementService.updateAllMeasurements(canvas, zoom);
  }, [canvas]);

  const getPointerPosition = useCallback((event: any): MeasurementPoint | null => {
    if (!canvas) return null;
    const pointer = canvas.getPointer(event.e);
    if (!pointer) return null;
    return { x: pointer.x, y: pointer.y };
  }, [canvas]);

  // Draw ruler preview based on current points and cursor position
  const drawRulerPreview = useCallback((currentCursorPos: MeasurementPoint) => {
    if (!canvas || distancePoints.length === 0) return;
    
    const zoom = canvas.getZoom();
    cleanupPreviewObjects();
    const newPreviewObjects: any[] = [];
    
    const startPoint = distancePoints[0];
    
    const distance = MeasurementService.calculateDistance(startPoint, currentCursorPos);
    
    // Ensure we have an updated calibration before calculating in mm
    calculatePixelsPerMm(null, canvas as CustomCanvas);
    const distanceInMm = pixelsToMillimeters(distance, canvas);

    const linePoints = [startPoint.x, startPoint.y, currentCursorPos.x, currentCursorPos.y] as [number, number, number, number];
    const lines = MeasurementService.createMeasurementLine(linePoints, zoom, false);
    
    const midpointX = (startPoint.x + currentCursorPos.x) / 2;
    const midpointY = (startPoint.y + currentCursorPos.y) / 2;
    
    const angle = Math.atan2(currentCursorPos.y - startPoint.y, currentCursorPos.x - startPoint.x) * (180 / Math.PI);
    
    const label = MeasurementService.createMeasurementLabel(
      `${Math.round(distanceInMm)}mm`,
      midpointX,
      midpointY,
      angle,
      false,
      zoom
    );

    newPreviewObjects.push(...lines, label);
    
    // Add cursor point preview
    const previewCircle = MeasurementService.createPointMarker(
      currentCursorPos.x, 
      currentCursorPos.y, 
      zoom, 
      false, 
      'rulerMeasurement'
    );
    newPreviewObjects.push(previewCircle);
    
    newPreviewObjects.forEach(obj => {
      ((obj as unknown) as CustomFabricObject).data = { isPreview: true };
      canvas.add(obj);
    });
    
    setPreviewObjects(newPreviewObjects);
    canvas.renderAll();
  }, [canvas, distancePoints, cleanupPreviewObjects]);

  // Draw angle preview based on current points and cursor position
  const drawAnglePreview = useCallback((currentCursorPos: MeasurementPoint) => {
    if (!canvas || anglePoints.length === 0) return;
    
    const zoom = canvas.getZoom();
    cleanupPreviewObjects();
    const newPreviewObjects: any[] = [];
    
    const previewCircle = MeasurementService.createPointMarker(
      currentCursorPos.x, 
      currentCursorPos.y, 
      zoom, 
      false, 
      'angleMeasurement'
    );
    newPreviewObjects.push(previewCircle);

    if (anglePoints.length === 1) {
      const linePoints = [
        anglePoints[0].x, anglePoints[0].y,
        currentCursorPos.x, currentCursorPos.y
      ] as [number, number, number, number];
      
      const lines = MeasurementService.createMeasurementLine(
        linePoints, 
        zoom, 
        false, 
        'angleMeasurement'
      );
      
      newPreviewObjects.push(...lines);
    } 
    else if (anglePoints.length === 2) {
      const line1Points = [
        anglePoints[0].x, anglePoints[0].y,
        anglePoints[1].x, anglePoints[1].y
      ] as [number, number, number, number];
      
      const line1 = MeasurementService.createMeasurementLine(
        line1Points, 
        zoom, 
        false, 
        'angleMeasurement'
      );
      
      const line2Points = [
        anglePoints[1].x, anglePoints[1].y,
        currentCursorPos.x, currentCursorPos.y
      ] as [number, number, number, number];
      
      const line2 = MeasurementService.createMeasurementLine(
        line2Points, 
        zoom, 
        false, 
        'angleMeasurement'
      );
      
      const angle = MeasurementService.calculateAngle(
        anglePoints[0], 
        anglePoints[1], 
        currentCursorPos
      );
      
      const angleLabel = MeasurementService.createMeasurementLabel(
        `${angle}°`,
        anglePoints[1].x,
        anglePoints[1].y - (15 / zoom),
        0,
        false,
        zoom,
        'angleMeasurement'
      );
      
      newPreviewObjects.push(...line1, ...line2, angleLabel);
    }
    
    newPreviewObjects.forEach(obj => {
      ((obj as unknown) as CustomFabricObject).data = { isPreview: true };
      canvas.add(obj);
    });
    
    setPreviewObjects(newPreviewObjects);
    canvas.renderAll();
  }, [canvas, anglePoints, cleanupPreviewObjects]);

  // Separate handler for ruler measurement mouse moves
  const handleRulerMouseMove = useCallback((event: any) => {
    if (!canvas || distancePoints.length === 0) return;
    
    const currentPoint = getPointerPosition(event);
    if (!currentPoint) return;

    // Update cursor position for tracking
    setCursorPosition(currentPoint);
    
    // Draw the preview with current cursor position
    drawRulerPreview(currentPoint);
  }, [
    canvas, 
    distancePoints,
    getPointerPosition,
    drawRulerPreview
  ]);

  // Separate handler for angle measurement mouse moves
  const handleAngleMouseMove = useCallback((event: any) => {
    if (!canvas || anglePoints.length === 0) return;
    
    const currentPoint = getPointerPosition(event);
    if (!currentPoint) return;

    // Update cursor position for tracking
    setCursorPosition(currentPoint);
    
    // Draw the preview with current cursor position
    drawAnglePreview(currentPoint);
  }, [
    canvas, 
    anglePoints,
    getPointerPosition,
    drawAnglePreview
  ]);

  const handleMeasurementClick = useCallback((event: any) => {
    if (activeTool !== 'ruler' && activeTool !== 'angle') return;
    
    const point = getPointerPosition(event);
    if (!point || !canvas) return;
    
    // Ensure we have an updated calibration
    calculatePixelsPerMm(null, canvas as CustomCanvas);
    
    if (activeTool === "ruler") {
      if (distancePoints.length === 0) {
        cleanupPreviewObjects();
        const zoom = canvas.getZoom();
        
        const pointMarker = MeasurementService.createPointMarker(
          point.x, 
          point.y, 
          zoom, 
          false, 
          'rulerMeasurement'
        );
        ((pointMarker as unknown) as CustomFabricObject).data = { isTemporary: true };
        
        canvas.add(pointMarker);
        setDistancePoints([point]);
        
        // Flag to update the preview
        shouldUpdatePreview.current = true;
        
        // Update cursor position to start showing preview
        setCursorPosition(point);
        
        // Draw initial preview at the same position
        drawRulerPreview(point);
        
        onToast?.({
          title: "First point placed",
          description: "Move and click to place the second point and complete the measurement.",
        });
      } 
      else if (distancePoints.length === 1) {
        const startPoint = distancePoints[0];
        const zoom = canvas.getZoom();
        
        const distance = MeasurementService.calculateDistance(startPoint, point);
        const distanceInMm = pixelsToMillimeters(distance, canvas);
        
        cleanupPreviewObjects();
        canvas.getObjects().forEach(obj => {
          if ((obj as any).data?.isTemporary) {
            canvas.remove(obj);
          }
        });

        const measurementId = `ruler-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        const point1 = MeasurementService.createPointMarker(
          startPoint.x, 
          startPoint.y, 
          zoom, 
          true, 
          'rulerMeasurement', 
          measurementId
        );
        
        const point2 = MeasurementService.createPointMarker(
          point.x, 
          point.y, 
          zoom, 
          true, 
          'rulerMeasurement', 
          measurementId
        );
        
        const linePoints = [startPoint.x, startPoint.y, point.x, point.y] as [number, number, number, number];
        const lines = MeasurementService.createMeasurementLine(
          linePoints, 
          zoom, 
          true, 
          'rulerMeasurement', 
          measurementId
        );
        
        const midpointX = (startPoint.x + point.x) / 2;
        const midpointY = (startPoint.y + point.y) / 2;
        const angle = Math.atan2(point.y - startPoint.y, point.x - startPoint.x) * (180 / Math.PI);
        
        const label = MeasurementService.createMeasurementLabel(
          `${Math.round(distanceInMm)}mm`,
          midpointX,
          midpointY,
          angle,
          true,
          zoom,
          'rulerMeasurement',
          measurementId
        );
        
        const group = new Group([point1, point2, ...lines, label], {
          selectable: false,
          evented: false,
        });
        
        ((group as unknown) as CustomFabricObject).data = { 
          type: 'rulerMeasurement',
          isPermanent: true, 
          measurementId,
          isMeasurementGroup: true,
          name: `Distance: ${Math.round(distanceInMm)}mm`
        };
        
        canvas.add(group);
        setDistancePoints([]);
        
        // Reset cursor position and preview flag
        setCursorPosition(null);
        shouldUpdatePreview.current = false;
        
        onToast?.({
          title: "Measurement complete",
          description: `Distance: ${Math.round(distanceInMm)}mm`,
        });

        onMeasurementComplete?.('ruler');
      }
    } 
    else if (activeTool === "angle") {
      const zoom = canvas.getZoom();
      
      if (anglePoints.length === 0) {
        cleanupPreviewObjects();
        
        const pointMarker = MeasurementService.createPointMarker(
          point.x, 
          point.y, 
          zoom, 
          false, 
          'angleMeasurement'
        );
        ((pointMarker as unknown) as CustomFabricObject).data = { isTemporary: true };
        
        canvas.add(pointMarker);
        setAnglePoints([point]);
        
        // Flag to update the preview
        shouldUpdatePreview.current = true;
        
        // Update cursor position to start showing preview
        setCursorPosition(point);
        
        // Draw initial preview at the same position
        drawAnglePreview(point);
        
        onToast?.({
          title: "First point placed",
          description: "Move and click to place the second point.",
        });
      } 
      else if (anglePoints.length === 1) {
        const pointMarker = MeasurementService.createPointMarker(
          point.x, 
          point.y, 
          zoom, 
          false, 
          'angleMeasurement'
        );
        ((pointMarker as unknown) as CustomFabricObject).data = { isTemporary: true };
        
        canvas.add(pointMarker);
        const updatedPoints = [...anglePoints, point];
        setAnglePoints(updatedPoints);
        
        // Reset the preview
        cleanupPreviewObjects();
        
        // Update cursor position to start showing preview for second line
        setCursorPosition(point);
        
        // Draw initial preview for the second line at the same position
        // We need to wait for state update, so we'll draw it in next render
        setTimeout(() => {
          if (canvas.disposed) return; // Safety check
          drawAnglePreview(point);
        }, 0);
        
        onToast?.({
          title: "Second point placed",
          description: "Move and click to place the final point and complete the angle measurement.",
        });
      } 
      else if (anglePoints.length === 2) {
        const angle = MeasurementService.calculateAngle(
          anglePoints[0], 
          anglePoints[1], 
          point
        );
        
        cleanupPreviewObjects();
        canvas.getObjects().forEach(obj => {
          if ((obj as any).data?.isTemporary) {
            canvas.remove(obj);
          }
        });

        const measurementId = `angle-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const points = [...anglePoints, point];
        
        const pointMarkers = points.map(p => 
          MeasurementService.createPointMarker(
            p.x, 
            p.y, 
            zoom, 
            true, 
            'angleMeasurement', 
            measurementId
          )
        );
        
        const lineGroups = [
          [points[0], points[1]],
          [points[1], points[2]]
        ].map(([p1, p2]) => {
          const linePoints = [p1.x, p1.y, p2.x, p2.y] as [number, number, number, number];
          return MeasurementService.createMeasurementLine(
            linePoints, 
            zoom, 
            true, 
            'angleMeasurement', 
            measurementId
          );
        });
        
        const angleLabel = MeasurementService.createMeasurementLabel(
          `${angle}°`,
          anglePoints[1].x,
          anglePoints[1].y - (15 / zoom),
          0,
          true,
          zoom,
          'angleMeasurement',
          measurementId
        );
        
        const group = new Group([...pointMarkers, ...lineGroups.flat(), angleLabel], {
          selectable: false,
          evented: false,
        });
        
        ((group as unknown) as CustomFabricObject).data = { 
          type: 'angleMeasurement',
          isPermanent: true, 
          isMeasurementGroup: true,
          measurementId,
          name: `Angle: ${angle}°`
        };
        
        canvas.add(group);
        setAnglePoints([]);
        
        // Reset cursor position and preview flag
        setCursorPosition(null);
        shouldUpdatePreview.current = false;
        
        onToast?.({
          title: "Angle measurement complete",
          description: `Angle: ${angle}°`,
        });

        onMeasurementComplete?.('angle');
      }
    }
  }, [
    activeTool, 
    canvas, 
    distancePoints, 
    anglePoints, 
    cleanupPreviewObjects, 
    getPointerPosition, 
    onToast,
    onMeasurementComplete,
    drawRulerPreview,
    drawAnglePreview
  ]);

  const clearMeasurements = useCallback(() => {
    setDistancePoints([]);
    setAnglePoints([]);
    if (canvas) {
      canvas.getObjects().forEach(obj => {
        const customObj = obj as any;
        if (customObj.data?.isTemporary || 
            customObj.data?.isPermanent || 
            customObj.data?.type === 'angleMeasurement' ||
            customObj.data?.type === 'rulerMeasurement') {
          canvas.remove(obj);
        }
      });
      canvas.renderAll();
    }
    
    // Reset cursor position and preview flag
    setCursorPosition(null);
    shouldUpdatePreview.current = false;
  }, [canvas]);

  // Track cursor movements and update preview on cursor position change
  useEffect(() => {
    if (!canvas || !cursorPosition || !shouldUpdatePreview.current) return;
    
    if (activeTool === 'ruler' && distancePoints.length > 0) {
      drawRulerPreview(cursorPosition);
    } else if (activeTool === 'angle' && anglePoints.length > 0) {
      drawAnglePreview(cursorPosition);
    }
  }, [
    cursorPosition,
    canvas,
    activeTool,
    distancePoints,
    anglePoints,
    drawRulerPreview,
    drawAnglePreview
  ]);

  useEffect(() => {
    if (!canvas) return;
    
    const isMeasuring = activeTool === 'ruler' || activeTool === 'angle';
    canvas.getObjects().forEach(obj => {
      if ((obj as any).data?.isTemporary || 
          (obj as any).data?.isPermanent || 
          (obj as any).data?.type === 'angleMeasurement' ||
          (obj as any).data?.type === 'rulerMeasurement') {
        return;
      }
      obj.selectable = !isMeasuring;
      obj.evented = !isMeasuring;
    });
    
    // Use the appropriate mouse move handler based on the active tool
    if (activeTool === 'ruler') {
      canvas.on('mouse:down', handleMeasurementClick);
      canvas.on('mouse:move', handleRulerMouseMove);
    } else if (activeTool === 'angle') {
      canvas.on('mouse:down', handleMeasurementClick);
      canvas.on('mouse:move', handleAngleMouseMove);
    } else {
      canvas.off('mouse:down', handleMeasurementClick);
      canvas.off('mouse:move', handleRulerMouseMove);
      canvas.off('mouse:move', handleAngleMouseMove);
      cleanupPreviewObjects();
      
      // Reset cursor position and preview flag when switching tools
      setCursorPosition(null);
      shouldUpdatePreview.current = false;
    }
    
    canvas.renderAll();
    
    return () => {
      canvas.off('mouse:down', handleMeasurementClick);
      canvas.off('mouse:move', handleRulerMouseMove);
      canvas.off('mouse:move', handleAngleMouseMove);
      cleanupPreviewObjects();
    };
  }, [
    activeTool, 
    canvas, 
    handleMeasurementClick, 
    handleRulerMouseMove, 
    handleAngleMouseMove,
    cleanupPreviewObjects
  ]);

  useEffect(() => {
    if (!canvas) return;
    
    const handleObjectRemoved = (e: any) => {
      if (!e.target) return;
      const obj = e.target as CustomFabricObject;
      
      if (obj.data?.type === 'rulerMeasurement') {
        resetMeasurement('ruler');
      } else if (obj.data?.type === 'angleMeasurement') {
        resetMeasurement('angle');
      }
    };
    
    canvas.on('object:removed', handleObjectRemoved);
    
    return () => {
      canvas.off('object:removed', handleObjectRemoved);
    };
  }, [canvas, resetMeasurement]);

  return {
    distancePoints,
    resetDistanceMeasurement: () => resetMeasurement('ruler'),
    
    anglePoints,
    resetAngleMeasurement: () => resetMeasurement('angle'),
    
    handleCanvasClick: handleMeasurementClick,
    updateMeasurementScale,
    clearMeasurements,
    
    measurePoints: activeTool === 'ruler' ? distancePoints : anglePoints,
  };
};
